import {
  Avatar,
  Box,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Modal, Button, DatePicker, message, Typography, Row, Divider } from "antd";
import { Controller } from "Controller/Controller";
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

function MemberTreatmentTable(props) {
  const { columnsData, tableData } = props;
  const [loading, setLoading] = useState(false);
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } =
    tableInstance;

  const iconColor = useColorModeValue("brand.500", "white");
  const textColor = useColorModeValue("navy.700", "white");

  // States for existing edit modal and date picker
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentID, setCurrentID] = useState(null);

  // States for detail modal
  const [openDetailRemaining, setOpenDetailRemaining] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [selectedServiceName, setSelectedServiceName] = useState("");

  // **New states for editing expiration date in the details modal**
  const [isEditExpirationModalOpen, setIsEditExpirationModalOpen] = useState(false);
  const [editExpirationDate, setEditExpirationDate] = useState(null);
  const [editExpirationDateID, setEditExpirationDateID] = useState(null);

  // Edit modal functions (existing)
  const handleEditClick = (expirationDate, id) => {
    setSelectedDate(expirationDate ? dayjs(expirationDate) : null);
    setCurrentID(id);
    setIsModalOpen(true);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleDateSubmit = async () => {
    setLoading(true);
    if (selectedDate && currentID) {
      const formattedDate = selectedDate.toISOString();
      const payload = {
        expiration_date: formattedDate,
        id: currentID,
      };
      try {
        const response = await Controller.updateExpireDate2(payload);
        if (response.status < 250) {
          message.success("Edited Successfully!");
          props.updateData();
        } else {
          message.error("Error: " + JSON.stringify(response.json));
        }
      } catch (e) {
        console.log(e);
        message.error("Internal server error");
      }
      setIsModalOpen(false);
      setSelectedDate(null);
      setCurrentID(null);
    }
    setLoading(false);
  };

  // Detail modal function (existing)
  const handleOpenModalDetailRemaining = async (service) => {
    try {
      const url = window.location.href;
      const parts = url.split("/");
      const customer_id = parts.pop();
      const response = await Controller.readDetailRemaining(service.service_id, customer_id);
      setDetailData(response);
      setSelectedServiceName(service.service_name);
      setOpenDetailRemaining(true);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch service details");
    }
  };

  // **New function to handle edit icon click**
  const handleEditExpiration = (service) => {
    setCurrentID(service.id);
    setEditExpirationDate(dayjs(service.expiration_date));
    setIsEditExpirationModalOpen(true);
  };

  // **New function to handle submission of new expiration date**
  const handleEditExpirationSubmit = async () => {
    setLoading(true);
    if (editExpirationDate && currentID) {
      const formattedDate = editExpirationDate.toISOString();
      const payload = {
        expiration_date: formattedDate,
        id: currentID,
      };
      try {
        const response = await Controller.updateExpireDate2(payload);
        if (response.status < 250) {
          message.success("Edited Successfully!");

          // Update main table data
          props.updateData();

          // Update detailData state to reflect changes in modal
          if (openDetailRemaining) {
            setDetailData(prevData =>
              prevData.map(membership => ({
                ...membership,
                services: membership.services.map(service =>
                  service.id === currentID
                    ? { ...service, expiration_date: formattedDate }
                    : service
                )
              }))
            );
          }
        } else {
          message.error("Error: " + JSON.stringify(response.json));
        }
      } catch (e) {
        console.log(e);
        message.error("Internal server error");
      }
      setIsEditExpirationModalOpen(false);
      setEditExpirationDate(null);
      setCurrentID(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (detailData) {
      console.log(page)
      for (var i in page) {
        if (page[i].id == detailData.id) {
          setDetailData(page)
        }
      }
    }

  }, [page])

  return (
    <>
      {/* Table Container */}
      <Flex
        direction="column"
        w="100%"
        overflowX={{ sm: "scroll", lg: "hidden" }}
      >
        <Flex
          align={{ sm: "flex-start", lg: "center" }}
          justify="space-between"
          w="100%"
          px="22px"
          pb="20px"
          mb="10px"
          boxShadow="0px 40px 58px -20px rgba(112, 144, 176, 0.26)"
        >
          <Text color={textColor} fontSize="xl" fontWeight="600">
            Treatments Remaining
          </Text>
        </Flex>

        {/* Main Table */}
        <Table {...getTableProps()} variant="simple" color="gray.500">
          <Thead>
            {headerGroups.map((headerGroup, index) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {columnsData.map((column, index) => (
                  <Th pe="10px" key={index} borderColor="transparent">
                    <Flex
                      justify="space-between"
                      align="center"
                      fontSize={{ sm: "10px", lg: "12px" }}
                      color="gray.400"
                    >
                      {column.Header}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              return (
                <Tr {...row.getRowProps()} key={index}>
                  <Td>{row.original.service_name}</Td>
                  <Td>{row.original.used}</Td>
                  <Td>{row.original.available}</Td>
                  <Td

                    style={{ cursor: "pointer" }}
                    onClick={() => handleOpenModalDetailRemaining(row.original)}
                  >
                    <EyeOutlined />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Flex>



      {/* Service Details Modal */}
      <Modal
        title={`Breakdown by Memberships`}
        visible={openDetailRemaining}
        onCancel={() => setOpenDetailRemaining(false)}
        footer={[
          <Button key="close" onClick={() => setOpenDetailRemaining(false)}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        {detailData.length > 0 ? (
          detailData.map((item, index) => (
            <Box key={index} mb={6}>
              <Divider>
                <Text fontSize="lg" fontWeight="bold" mb={2}>
                  {item.name}
                </Text>
              </Divider>

              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th style={{ width: "200px" }}>Service</Th>

                    <Th>Used</Th>
                    <Th>Remaining</Th>
                    <Th>Expiration Date</Th>
                    <Th>Activation Date</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {item?.services && item?.services.length > 0 && item?.services.map((service, serviceIndex) => (
                    <Tr key={serviceIndex}>
                      <Td style={{ width: "200px" }}>{service.name}</Td>

                      <Td>{service.used}</Td>
                      <Td>{service.remaining}</Td>
                      <Td>
                        {/* Add edit icon in front of expiration date */}
                        <EditOutlined
                          style={{ marginRight: "10px", cursor: "pointer" }}
                          onClick={() => handleEditExpiration(service)}
                        />
                        {dayjs(service.expiration_date).format("YYYY-MM-DD HH:mm:ss")}
                      </Td>
                      <Td>
                    
                        {dayjs(service.activation_date).format("YYYY-MM-DD HH:mm:ss")}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ))
        ) : (
          <Text>No details available</Text>
        )}
      </Modal>

      {/* New Modal for Editing Expiration Date */}
      <Modal
        title="Edit Expiration Date"
        visible={isEditExpirationModalOpen}
        onCancel={() => setIsEditExpirationModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsEditExpirationModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleEditExpirationSubmit}
          >
            Submit
          </Button>,
        ]}
        width={500}
      >
        <Typography.Text style={{ marginBottom: "8px", fontWeight: "500" }}>
          Select Expiration Date and Time
        </Typography.Text>
        <br />
        <Row style={{ width: "100%" }}>
          <DatePicker
            showTime
            value={editExpirationDate}
            onChange={(date) => setEditExpirationDate(date)}
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%" }}
            placeholder="Select date and time"
          />
        </Row>
      </Modal>
    </>
  );
}

export default MemberTreatmentTable;